import { HttpClient, profileApi } from '@/common';
import { BackendService } from '@/types/BackendService';
import { UserProfile } from '@/types/UserProfile.type';
import { AxiosResponse } from 'axios';
import { create } from 'zustand';

type UserProfileStore = {
	userProfile: UserProfile;
	allUserProfiles: UserProfile[];
	loading: boolean;
	setUserProfile: (userProfile: Partial<UserProfile>) => void;
	setAllUserProfiles: (userProfiles: UserProfile[]) => void;
	getAllUserProfiles: () => Promise<void>;
	updateProfile: (data: { fullName?: string; phoneNumber?: string }) => Promise<boolean>;
	softDeleteUser: (uid: string, companyUid: string) => Promise<boolean>;
	updateAvatar: (avatar: File) => Promise<void>;
};

const useUserStore = create<UserProfileStore>((set) => ({
	userProfile: {
		email: '',
		fullName: '',
		role: '',
		company: {
			uid: '',
			name: '',
		},
		avatar: '',
		phoneNumber: '',
		location: {
			country: '',
			state: '',
			city: '',
			postalCode: '',
		},
	},

	allUserProfiles: [],
	loading: false,

	setUserProfile: (userProfile: Partial<UserProfile>) => {
		set((prevState) => ({
			userProfile: {
				...prevState.userProfile,
				...userProfile,
			},
		}));
	},

	setAllUserProfiles: (allUserProfiles: UserProfile[]) => {
		set({ allUserProfiles });
	},

	getAllUserProfiles: async () => {
		set({ loading: true });
		try {
			const res: AxiosResponse<UserProfile[]> = await profileApi.getAllUserProfile();
			set({ allUserProfiles: res.data });
		} catch (error) {
			console.error('Error fetching user profiles:', error);
		} finally {
			set({ loading: false });
		}
	},

	updateProfile: async (data: { fullName?: string; phoneNumber?: string }) => {
		set({ loading: true });
		try {
			const state = useUserStore.getState();
			const res = await profileApi.updateUserProfile(state.userProfile.email!, data);

			if (!res.data.error) {
				set((state) => ({
					userProfile: {
						...state.userProfile,
						...data,
					},
				}));
				return true;
			}
			return false;
		} catch (error) {
			console.error('Error updating profile:', error);
			return false;
		} finally {
			set({ loading: false });
		}
	},

	softDeleteUser: async (uid: string, companyUid: string) => {
		set({ loading: true });
		try {
			const response = await profileApi.softDeleteUser({ uid, companyUid });

			if (response && 'error' in response && response.error === false) {
				await useUserStore.getState().getAllUserProfiles();
				return true;
			}
			return false;
		} catch (error) {
			console.error('Error soft deleting user:', error);
			return false;
		} finally {
			set({ loading: false });
		}
	},

	updateAvatar: async (avatar: File) => {
		try {
			set({ loading: true });
			const authClient = HttpClient(BackendService.Auth);

			const formData = new FormData();
			formData.append('file', avatar, avatar.name);

			const result = await authClient.post('/upload-avatar', formData, {
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			});

			if (result.data.error) {
				throw new Error('Error updating avatar');
			}

			set((state) => ({
				userProfile: {
					...state.userProfile,
					avatar: result.data.userProfile.avatar,
				},
			}));
		} catch (error) {
			console.error('Error updating avatar:', error);
		} finally {
			set({ loading: false });
		}
	},
}));

export default useUserStore;
